/* eslint-disable no-restricted-globals */
import { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "../lib/supabase";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { P } from "../styles/theme";
import { DEPOSIT_AMOUNT, MAX_PHOTOS } from "../constants/config";
import { logError } from "../utils/logger";
import { computeAvailableSlots } from "../utils/slots";
import { convertToWebP } from "../utils/imageUtils";
import { normalizePhone, isValidAustralianPhone } from "../utils/validators";
import { useRecaptcha } from "../hooks/useRecaptcha";
import { insertBooking } from "../services/bookingService";
import { createPaymentIntent, processRefund } from "../services/paymentService";
import { sendEmailViaEdge } from "../services/emailService";
import { createCalendarEvent, setCalendarEventId } from "../services/calendarService";
import { uploadPhotos } from "../services/photoService";
import { upsertClient, fetchServicesAndShopData } from "../services/clientService";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function BookingFlow({ session, onViewMyBookings, onFindBooking }) {
  const stripe   = useStripe();
  const elements = useElements();

  // ── Mode & Auth ───────────────────────────────────────────
  const [pageMode,     setPageMode]     = useState('steps');
  const [isGuest,      setIsGuest]      = useState(false);
  const [guestName,    setGuestName]    = useState('');
  const [guestEmail,   setGuestEmail]   = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError,   setLoginError]   = useState('');

  // ── Booking details ───────────────────────────────────────
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const [hasAllergy,   setHasAllergy]   = useState(null);

  const [categories,       setCategories]       = useState([]);
  const [services,         setServices]         = useState([]);
  const [shopHours,        setShopHours]        = useState([]);
  const [holidays,         setHolidays]         = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);
  const [lunchBreak,       setLunchBreak]       = useState(null);

  // selections: { [categoryId]: serviceId (single) | serviceId[] (multi) }
  const [selections,    setSelections]    = useState({});
  const [selectedDate,  setSelectedDate]  = useState('');
  const [selectedTime,  setSelectedTime]  = useState('');
  const [clientPhone,   setClientPhone]   = useState('');

  // ── Photos ────────────────────────────────────────────────
  const [photos,        setPhotos]        = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const photoInputRef = useRef(null);

  // ── Payment ───────────────────────────────────────────────
  const [recaptchaToken,  setRecaptchaToken]  = useState('');
  const { renderCaptcha, captchaRequired }    = useRecaptcha(setRecaptchaToken, 'recaptcha-container');
  const [isPaying,        setIsPaying]        = useState(false);
  const [paymentError,    setPaymentError]    = useState('');
  const [bookingConfirmRef, setBookingConfirmRef] = useState('');
  const [quantities,        setQuantities]        = useState({});

  // ── Derived identity ──────────────────────────────────────
  const clientName  = isGuest
    ? guestName.trim()
    : (session?.user?.user_metadata?.full_name || session?.user?.email?.split('@')[0] || '');
  const clientEmail = isGuest
    ? guestEmail.trim().toLowerCase()
    : (session?.user?.email || '');

  // ── Services & slots ──────────────────────────────────────
  const isQtyService = (name) => ['Nail Repair', 'Single Nail Extension'].some(n => n.toLowerCase() === (name || '').toLowerCase());

  const getCatServices = (catId) =>
    services
      .filter(s => s.category_id === catId)
      .sort((a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity));

  // Categories that have at least one service (skip empty ones)
  const activeCategories = useMemo(() =>
    categories.filter(cat => services.some(s => s.category_id === cat.id)),
    [categories, services]);

  // Flatten all selected service objects in category order
  const allSelectedServices = useMemo(() => {
    const result = [];
    categories.forEach(cat => {
      const sel = selections[cat.id];
      if (!sel) return;
      if (cat.selection_type === 'single') {
        const svc = services.find(s => s.id === sel);
        if (svc) result.push(svc);
      } else {
        (Array.isArray(sel) ? sel : []).forEach(id => {
          const svc = services.find(s => s.id === id);
          if (svc) result.push(svc);
        });
      }
    });
    return result;
  }, [categories, services, selections]);

  const totalPrice = allSelectedServices.reduce((s, svc) => {
    const qty = isQtyService(svc.name) ? (quantities[svc.id] || 1) : 1;
    return s + Number(svc.price || 0) * qty;
  }, 0);
  const totalDuration        = allSelectedServices.reduce((s, svc) => s + Number(svc.duration || 0), 0);
  const selectedServiceNames = allSelectedServices.map(s => {
    if (isQtyService(s.name)) {
      const qty = quantities[s.id] || 1;
      return qty > 1 ? `${s.name} ×${qty}` : s.name;
    }
    return s.name;
  }).filter(Boolean).join(' · ');
  const balanceDue = Math.max(totalPrice - DEPOSIT_AMOUNT, 0);

  useEffect(() => {
    fetchServicesAndShopData().then(({ categories: cats, services: svcs, shopHours: h, holidays: hol, existingBookings: slots, lunchBreak: lb }) => {
      setCategories(cats); setServices(svcs); setShopHours(h); setHolidays(hol); setExistingBookings(slots); setLunchBreak(lb);
    });
  }, []);

  useEffect(() => { if (pageMode === 'payment') setTimeout(renderCaptcha, 100); }, [pageMode, renderCaptcha]);

  const minBookingDate = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const availableSlots = useMemo(() => {
    const slots = computeAvailableSlots({ date: selectedDate, shopHours, holidays, existingBookings, durationMins: totalDuration || 60, lunchBreak });
    const cutoff = Date.now() + 24 * 60 * 60 * 1000;
    return slots.filter(t => new Date(`${selectedDate}T${t}:00`).getTime() > cutoff);
  }, [selectedDate, totalDuration, shopHours, holidays, existingBookings, lunchBreak]);

  // ── Step completion ───────────────────────────────────────
  const guestInfoComplete = isGuest && guestName.trim().length >= 2 && guestEmail.includes('@');
  const step1Done = !!session || guestInfoComplete;
  const step2Done = step1Done && policyAgreed && hasAllergy === false;

  // All single-select categories must have a selection
  const serviceSelectionComplete = useMemo(() =>
    activeCategories.length > 0 &&
    activeCategories.every(cat => cat.selection_type !== 'single' || !!selections[cat.id]),
    [activeCategories, selections]);

  const step3Done = step2Done && serviceSelectionComplete && !!selectedDate && !!selectedTime && isValidAustralianPhone(clientPhone);

  // ── Service selection handlers ────────────────────────────
  const handleSingleSelect = (catId, svcId) => {
    setSelections(prev => ({ ...prev, [catId]: prev[catId] === svcId ? null : svcId }));
    setSelectedDate(''); setSelectedTime('');
  };
  const handleMultiToggle = (catId, svcId) => {
    const svc = services.find(s => s.id === svcId);
    const cur = Array.isArray(selections[catId]) ? selections[catId] : [];
    const removing = cur.includes(svcId);
    const next = removing ? cur.filter(id => id !== svcId) : [...cur, svcId];
    setSelections(prev => ({ ...prev, [catId]: next }));
    if (isQtyService(svc?.name)) {
      if (removing) setQuantities(q => { const nq = { ...q }; delete nq[svcId]; return nq; });
      else setQuantities(q => ({ ...q, [svcId]: 1 }));
    }
    setSelectedDate(''); setSelectedTime('');
  };

  // ── Photos ────────────────────────────────────────────────
  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    const toAdd = files.slice(0, MAX_PHOTOS - photos.length);
    if (!toAdd.length) return;
    const valid = toAdd.filter(f => f.size <= 10 * 1024 * 1024);
    if (valid.length < toAdd.length) alert('Some files exceed 10 MB and were skipped.');
    setPhotos(prev => [...prev, ...valid]);
    setPhotoPreviews(prev => [...prev, ...valid.map(f => URL.createObjectURL(f))]);
    e.target.value = '';
  };
  const removePhoto = (idx) => {
    URL.revokeObjectURL(photoPreviews[idx]);
    setPhotos(prev => prev.filter((_, i) => i !== idx));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Auth ──────────────────────────────────────────────────
  const handleGoogleLogin = async () => {
    setLoginLoading(true); setLoginError('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options:  { redirectTo: window.location.origin, queryParams: { access_type: 'offline', prompt: 'consent' } },
    });
    if (error) { setLoginError(error.message); setLoginLoading(false); }
  };

  const handleChooseGuest = () => {
    setIsGuest(true); setLoginError('');
  };
  const handleBackToChoice = () => {
    setIsGuest(false); setGuestName(''); setGuestEmail(''); setLoginError('');
  };

  // ── Payment & booking ─────────────────────────────────────
  const handlePaymentAndSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (captchaRequired && !recaptchaToken) {
      setPaymentError('Please complete the reCAPTCHA verification.');
      return;
    }

    setIsPaying(true); setPaymentError('');
    let capturedPaymentIntentId = null;

    try {
      const clientSecret  = await createPaymentIntent({ amount: DEPOSIT_AMOUNT * 100, currency: 'aud', recaptchaToken, clientEmail });
      const normalizedPhone = normalizePhone(clientPhone.trim());

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { name: clientName, email: clientEmail, phone: normalizedPhone, address: { country: 'AU' } },
        },
      });
      if (confirmError) { setPaymentError(confirmError.message); setIsPaying(false); return; }
      if (paymentIntent.status !== 'succeeded') { setPaymentError('Payment was not completed.'); setIsPaying(false); return; }
      capturedPaymentIntentId = paymentIntent.id;

      const bookingTimestamp = new Date(`${selectedDate}T${selectedTime}:00`).toISOString();

      // ── DB insert — handles both guest and authenticated ──
      let newBooking;
      try {
        newBooking = await insertBooking({
          client_name:              clientName,
          client_phone:             normalizedPhone,
          client_email:             clientEmail,
          service_name:             selectedServiceNames,
          booking_date:             bookingTimestamp,
          total_amount:             totalPrice,
          deposit_amount:           DEPOSIT_AMOUNT,
          balance_due:              balanceDue,
          status:                   'pending',
          has_allergy:              hasAllergy,
          stripe_payment_intent_id: paymentIntent.id,
          total_duration:           totalDuration || 60,
          is_guest:                 isGuest,
        });
      } catch (dbErr) {
        if (dbErr.code === '23505') {
          // Slot conflict — auto-refund
          try { await processRefund(capturedPaymentIntentId); } catch (refErr) {
            await logError(supabase, 'stripe_refund', `Auto-refund failed after slot conflict: ${refErr.message}`, { payment_intent_id: capturedPaymentIntentId });
          }
          setPaymentError('This time slot was just taken by another booking. Your payment has been fully refunded — it will appear within 5–10 business days. Please go back and select a different time.');
          setIsPaying(false);
          return;
        }
        throw dbErr;
      }

      if (newBooking?.booking_reference) setBookingConfirmRef(newBooking.booking_reference);

      // Auto-register client (works for both anon and authenticated)
      if (clientEmail) {
        upsertClient({ name: clientName, phone: normalizedPhone, email: clientEmail });
      }

      // Photos (authenticated users only — guest photo upload is a future feature)
      if (photos.length > 0 && newBooking && !isGuest) {
        uploadPhotos(newBooking.id, photos, convertToWebP, (type, msg, ctx) => logError(supabase, type, msg, ctx)).catch(() => {});
      }

      // Calendar & emails
      if (newBooking) {
        createCalendarEvent({
          id: newBooking.id, client_name: clientName, client_email: clientEmail,
          client_phone: normalizedPhone, service_name: selectedServiceNames,
          booking_date: bookingTimestamp, total_duration: totalDuration || 60,
          deposit_amount: DEPOSIT_AMOUNT, balance_due: balanceDue,
          has_allergy: hasAllergy, status: 'pending',
        }).then(calData => { if (calData?.event_id) setCalendarEventId(newBooking.id, calData.event_id); });

        const emailPayload = {
          id: newBooking.id, client_name: clientName, client_email: clientEmail,
          client_phone: normalizedPhone, service_name: selectedServiceNames,
          booking_date: bookingTimestamp, deposit_amount: DEPOSIT_AMOUNT,
          balance_due: balanceDue, total_amount: totalPrice, has_allergy: hasAllergy,
          booking_reference: newBooking.booking_reference,
        };
        sendEmailViaEdge('booking_pending', emailPayload, newBooking.id);
        sendEmailViaEdge('owner_booking_notification', emailPayload, newBooking.id);
      }

      setPageMode('confirmed');
    } catch (err) {
      setPaymentError(err.message);
      await logError(supabase, 'stripe_payment', err.message, { client_email: clientEmail, payment_intent_id: capturedPaymentIntentId });
    }
    setIsPaying(false);
  };

  const resetBooking = () => {
    setSelections({}); setQuantities({});
    setPolicyAgreed(false); setHasAllergy(null);
    setSelectedDate(''); setSelectedTime(''); setClientPhone('');
    setPaymentError(''); setPageMode('steps');
    setPhotos([]); setPhotoPreviews([]); setRecaptchaToken('');
    setIsGuest(false); setGuestName(''); setGuestEmail('');
    setBookingConfirmRef('');
  };

  // ── Shared styles ─────────────────────────────────────────
  const cs = {
    container: {
      maxWidth: '450px', margin: '0 auto', background: P.bg, minHeight: '100vh',
      padding: '20px 16px 40px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: P.text,
    },
    card: {
      background: P.card, borderRadius: '16px', padding: '20px',
      border: `1px solid ${P.border}`, boxShadow: '0 4px 14px rgba(139,94,87,0.04)',
      marginBottom: '16px',
    },
    input: {
      width: '100%', padding: '13px', borderRadius: '8px',
      border: `1.5px solid ${P.border}`, outline: 'none',
      boxSizing: 'border-box', fontSize: '16px',
      WebkitAppearance: 'none', appearance: 'none', fontFamily: 'inherit',
    },
    primaryBtn: (disabled) => ({
      width: '100%', padding: '16px', borderRadius: '12px', border: 'none',
      background: disabled ? P.border : `linear-gradient(135deg, ${P.rose}, ${P.deep})`,
      color: disabled ? P.muted : '#fff', fontSize: '16px', fontWeight: 700,
      cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s', minHeight: '52px',
    }),
    label:        { fontSize: '11px', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' },
    sectionTitle: { fontSize: '13px', fontWeight: 700, color: P.deep, marginBottom: '10px' },
    yesNoBtn:     (active, color) => ({ flex: 1, padding: '13px', borderRadius: '10px', border: `2px solid ${active ? color : P.border}`, background: active ? color + '15' : P.white, color: active ? color : P.text, fontWeight: 700, cursor: 'pointer', fontSize: '15px', minHeight: '50px' }),
    divider:      { display: 'flex', alignItems: 'center', gap: '12px', margin: '14px 0' },
    dividerLine:  { flex: 1, height: '1px', background: P.border },
  };

  const StepBadge = ({ number, done, locked }) => (
    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: done ? '#6EA882' : locked ? P.border : P.rose, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>
      {done ? '✓' : number}
    </div>
  );

  // ── PAYMENT PAGE ──────────────────────────────────────────
  if (pageMode === 'payment') {
    const cardElementOptions = {
      style: { base: { fontSize: '16px', color: P.text, '::placeholder': { color: P.muted } }, invalid: { color: '#E15F5F' } },
      hidePostalCode: true,
    };
    return (
      <div style={cs.container}>
        {isPaying && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '40px' }}>💳</div>
            <p style={{ color: '#fff', fontWeight: 700, fontSize: '18px', margin: 0 }}>Processing payment...</p>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '13px', margin: 0 }}>Do not close this window</p>
          </div>
        )}
        <div style={{ textAlign: 'center', marginBottom: '24px', paddingTop: '10px' }}>
          <h1 style={{ fontSize: '26px', color: P.deep, fontWeight: 600 }}>Nails by Aurora</h1>
          <p style={{ fontSize: '12px', color: P.muted, marginTop: '4px' }}>Seabrook Home Studio</p>
        </div>
        <div style={{ ...cs.card, background: P.peach + '20', textAlign: 'center' }}>
          <p style={{ fontSize: '13px', color: P.muted, marginBottom: '6px' }}>Booking Deposit</p>
          <h2 style={{ fontSize: '32px', color: P.rose, margin: '0 0 6px', fontWeight: 700 }}>${DEPOSIT_AMOUNT}.00 AUD</h2>
          <p style={{ fontSize: '13px', color: P.text, fontWeight: 600 }}>{selectedServiceNames}</p>
          <p style={{ fontSize: '12px', color: P.muted, marginTop: '4px' }}>
            {new Date(`${selectedDate}T${selectedTime}:00`).toLocaleString('en-AU', { weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
          <p style={{ fontSize: '12px', color: P.deep, marginTop: '6px', fontWeight: 600 }}>
            Total: ${totalPrice} · Balance of ${balanceDue} due at studio
          </p>
        </div>
        <div style={cs.card}>
          <label style={cs.label}>Credit or Debit Card</label>
          <div style={{ padding: '16px 14px', borderRadius: '8px', border: `1.5px solid ${P.border}`, background: P.white }}>
            <CardElement options={cardElementOptions} />
          </div>
          {paymentError && <p style={{ fontSize: '13px', color: P.red, marginTop: '10px', fontWeight: 600 }}>⚠️ {paymentError}</p>}
        </div>
        {captchaRequired && <div style={{ ...cs.card, display: 'flex', justifyContent: 'center' }}><div id="recaptcha-container" /></div>}
        <button onClick={handlePaymentAndSubmit} disabled={isPaying || !stripe || (captchaRequired && !recaptchaToken)}
          style={{ ...cs.primaryBtn(isPaying || !stripe || (captchaRequired && !recaptchaToken)), background: isPaying ? P.muted : '#6EA882' }}>
          {isPaying ? 'Processing...' : `Pay $${DEPOSIT_AMOUNT} & Request Booking`}
        </button>
        <button onClick={() => setPageMode('steps')} disabled={isPaying}
          style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: 'transparent', color: P.muted, fontSize: '15px', cursor: 'pointer', marginTop: '8px', minHeight: '44px' }}>
          ← Back
        </button>
      </div>
    );
  }

  // ── CONFIRMED PAGE ────────────────────────────────────────
  if (pageMode === 'confirmed') {
    return (
      <div style={{ ...cs.container, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...cs.card, textAlign: 'center', padding: '40px 24px' }}>
          <span style={{ fontSize: '48px' }}>💖</span>
          <h2 style={{ color: P.deep, margin: '16px 0 8px', fontWeight: 600 }}>Booking Request Received!</h2>
          <p style={{ fontSize: '14px', color: P.muted, lineHeight: '1.7' }}>
            Your deposit has been charged.<br />
            We'll email you at <strong>{clientEmail}</strong> once confirmed.
          </p>

          {/* Booking reference — shown for both guests and authenticated */}
          {bookingConfirmRef && (
            <div style={{ margin: '20px 0', padding: '16px', background: P.peach + '30', borderRadius: '12px', border: `1.5px solid ${P.rose}40` }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                Your Booking Reference
              </p>
              <p style={{ fontSize: '28px', fontWeight: 800, color: P.deep, letterSpacing: '0.1em', margin: 0, fontFamily: 'monospace' }}>
                {bookingConfirmRef}
              </p>
              {isGuest && (
                <p style={{ fontSize: '12px', color: P.muted, marginTop: '8px' }}>
                  Save this reference — you'll need it to view or cancel your booking.
                </p>
              )}
            </div>
          )}

          <p style={{ fontSize: '14px', color: P.muted, lineHeight: '1.7' }}>
            Thank you, {clientName}! 🌸
          </p>

          {/* Authenticated users see My Bookings */}
          {session && !isGuest && (
            <button onClick={onViewMyBookings}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', border: `1.5px solid ${P.border}`, background: P.white, color: P.deep, fontSize: '15px', fontWeight: 600, cursor: 'pointer', marginTop: '16px', minHeight: '50px' }}>
              View My Bookings
            </button>
          )}
          {/* Guests see Find My Booking */}
          {isGuest && (
            <button onClick={onFindBooking}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', border: `1.5px solid ${P.border}`, background: P.white, color: P.deep, fontSize: '15px', fontWeight: 600, cursor: 'pointer', marginTop: '16px', minHeight: '50px' }}>
              Find My Booking
            </button>
          )}
          <button onClick={resetBooking} style={{ ...cs.primaryBtn(false), marginTop: '10px' }}>Book Another Session</button>
        </div>
      </div>
    );
  }

  // ── MAIN BOOKING FLOW ─────────────────────────────────────
  return (
    <div style={cs.container}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '24px', paddingTop: '10px' }}>
        <h1 style={{ fontSize: '28px', color: P.deep, fontWeight: 600, letterSpacing: '-0.02em' }}>Nails by Aurora</h1>
        <p style={{ fontSize: '12px', color: P.muted, marginTop: '4px' }}>Seabrook Home Studio · Point Cook area</p>
        {session && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            {session.user.user_metadata?.avatar_url && <img src={session.user.user_metadata.avatar_url} alt="avatar" style={{ width: '22px', height: '22px', borderRadius: '50%' }} />}
            <span style={{ fontSize: '12px', color: P.muted }}>{clientEmail}</span>
            <button onClick={onViewMyBookings} style={{ fontSize: '12px', color: P.rose, background: P.peach + '50', border: `1px solid ${P.border}`, borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', fontWeight: 600, minHeight: '32px' }}>My Bookings</button>
            <button onClick={() => supabase.auth.signOut()} style={{ fontSize: '11px', color: P.muted, background: 'none', border: `1px solid ${P.border}`, borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', minHeight: '32px' }}>Sign out</button>
          </div>
        )}
        {!session && (
          <div style={{ marginTop: '8px' }}>
            <button onClick={onFindBooking} style={{ fontSize: '12px', color: P.muted, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              Already booked? Find my booking →
            </button>
          </div>
        )}
      </div>

      {/* ── STEP 1: Authentication or Guest ── */}
      <div style={cs.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <StepBadge number={1} done={step1Done} locked={false} />
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: P.deep, margin: 0 }}>
            {step1Done
              ? (isGuest ? `Continuing as Guest` : 'Signed In')
              : 'How would you like to continue?'}
          </h3>
        </div>

        {/* Already authenticated with Google */}
        {session && !isGuest && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {session.user.user_metadata?.avatar_url && <img src={session.user.user_metadata.avatar_url} alt="avatar" style={{ width: '44px', height: '44px', borderRadius: '50%' }} />}
            <div>
              <p style={{ fontWeight: 700, color: P.deep, fontSize: '15px', margin: 0 }}>{clientName}</p>
              <p style={{ fontSize: '12px', color: P.muted, margin: '2px 0 0' }}>{clientEmail}</p>
            </div>
          </div>
        )}

        {/* Guest form */}
        {isGuest && !session && (
          <div>
            <div style={{ marginBottom: '12px' }}>
              <label style={cs.label}>Your Name</label>
              <input style={cs.input} placeholder="Sarah Jones" value={guestName} onChange={e => setGuestName(e.target.value)} />
            </div>
            <div style={{ marginBottom: '4px' }}>
              <label style={cs.label}>Email Address</label>
              <input type="email" style={cs.input} placeholder="sarah@example.com" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} />
              <p style={{ fontSize: '11px', color: P.muted, marginTop: '6px' }}>
                Your booking reference and confirmation will be sent here.
              </p>
            </div>
            <button onClick={handleBackToChoice} style={{ marginTop: '10px', background: 'none', border: 'none', color: P.muted, fontSize: '13px', cursor: 'pointer', padding: 0 }}>
              ← Sign in with Google instead
            </button>
          </div>
        )}

        {/* Choice screen (no session, no guest mode) */}
        {!session && !isGuest && (
          <div>
            {/* Guest — primary option */}
            <button onClick={handleChooseGuest}
              style={{ width: '100%', padding: '16px', borderRadius: '12px', border: `2px solid ${P.rose}`, background: P.rose, color: '#fff', fontSize: '16px', fontWeight: 700, cursor: 'pointer', marginBottom: '12px', minHeight: '52px' }}>
              Continue as Guest
            </button>

            <div style={cs.divider}>
              <div style={cs.dividerLine} />
              <span style={{ fontSize: '12px', color: P.muted, whiteSpace: 'nowrap' }}>or sign in for booking history</span>
              <div style={cs.dividerLine} />
            </div>

            {/* Google — secondary option */}
            <button onClick={handleGoogleLogin} disabled={loginLoading}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', border: `1.5px solid ${P.border}`, background: P.white, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', fontSize: '15px', fontWeight: 600, color: P.text, cursor: loginLoading ? 'not-allowed' : 'pointer', opacity: loginLoading ? 0.7 : 1, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', minHeight: '50px' }}>
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {loginLoading ? 'Redirecting...' : 'Continue with Google'}
            </button>
            {loginError && <p style={{ fontSize: '13px', color: P.red, marginTop: '10px' }}>{loginError}</p>}
          </div>
        )}
      </div>

      {/* ── STEP 2: Policy & Allergy ── */}
      <div style={{ ...cs.card, opacity: step1Done ? 1 : 0.45, pointerEvents: step1Done ? 'auto' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <StepBadge number={2} done={step2Done} locked={!step1Done} />
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: step1Done ? P.deep : P.muted, margin: 0 }}>Policy & Allergy Check</h3>
        </div>
        <div style={{ background: '#FFFDFB', border: `1px solid ${P.rose}50`, borderRadius: '12px', padding: '16px', fontSize: '13px', lineHeight: '1.75', color: P.text, marginBottom: '16px' }}>
          <p style={{ fontWeight: 700, color: P.deep, marginBottom: '10px' }}>📋 Studio Policies — Please Read</p>
          <p style={{ marginBottom: '8px' }}><strong>Deposit & Payment</strong><br />A ${DEPOSIT_AMOUNT} deposit is required to secure your appointment. Balance due on the day by cash or card.</p>
          <p style={{ marginBottom: '8px' }}><strong>Cancellation & Rescheduling</strong><br />Changes must be made at least <strong>48 hours in advance</strong>. Cancellations within 48 hours forfeit the deposit.</p>
          <p style={{ marginBottom: '8px' }}><strong>Late Arrival</strong><br /><strong>10-minute</strong> grace period. Later arrivals may result in shortened or cancelled service.</p>
          <p style={{ marginBottom: '8px' }}><strong>Health & Safety</strong><br />All tools are sterilised between every client. Please reschedule if unwell.</p>
          <p style={{ marginBottom: '12px' }}><strong>Children & Guests</strong><br />Please do not bring additional guests unless receiving a service.</p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', paddingTop: '10px', borderTop: `1px dashed ${P.border}`, fontSize: '14px', fontWeight: 600 }}>
            <input type="checkbox" checked={policyAgreed} onChange={e => setPolicyAgreed(e.target.checked)} style={{ width: '20px', height: '20px', accentColor: P.rose, flexShrink: 0 }} />
            I have read and agree to all booking policies
          </label>
        </div>
        <p style={cs.sectionTitle}>⚠️ Allergy Disclosure</p>
        <p style={{ fontSize: '13px', color: P.text, marginBottom: '14px', lineHeight: '1.6' }}>
          Do you have a known allergy or sensitivity to:<br />
          <strong>Isopropyl alcohol · Acetone · Gel/acrylic monomer · HEMA</strong>
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setHasAllergy(false)} style={cs.yesNoBtn(hasAllergy === false, '#6EA882')}>✓ No allergies</button>
          <button onClick={() => setHasAllergy(true)}  style={cs.yesNoBtn(hasAllergy === true,  P.red)}>✗ Yes, I have allergies</button>
        </div>
        {hasAllergy === true && (
          <div style={{ marginTop: '14px', padding: '16px', borderRadius: '12px', background: '#FFF0F0', border: `1px solid ${P.red}60` }}>
            <p style={{ fontSize: '13px', color: P.red, fontWeight: 700, lineHeight: '1.7', marginBottom: '14px' }}>
              ⚠️ We're unable to accept online bookings from clients with these allergies.<br /><br />Please reach out directly — we'd love to help!
            </p>
            <a href="https://www.instagram.com/nails.by.aurora.melb" target="_blank" rel="noopener noreferrer"
              style={{ display: 'block', padding: '14px', borderRadius: '10px', background: 'linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', color: '#fff', fontWeight: 700, fontSize: '15px', textAlign: 'center', textDecoration: 'none', minHeight: '50px', lineHeight: '1.4' }}>
              📩 DM us on Instagram @nails.by.aurora.melb
            </a>
          </div>
        )}
      </div>

      {/* ── STEP 3: Service + Date/Time + Phone ── */}
      <div style={{ ...cs.card, opacity: step2Done ? 1 : 0.45, pointerEvents: step2Done ? 'auto' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <StepBadge number={3} done={false} locked={!step2Done} />
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: step2Done ? P.deep : P.muted, margin: 0 }}>Select Service & Time</h3>
        </div>

        {/* Dynamic categories from DB */}
        {activeCategories.length === 0 ? (
          <p style={{ fontSize: '13px', color: P.muted, marginBottom: '20px' }}>
            No services available at this time. Please check back later.
          </p>
        ) : activeCategories.map(cat => {
          const catSvcs = getCatServices(cat.id);
          const isSingle = cat.selection_type === 'single';
          const catSel   = selections[cat.id];

          return (
            <div key={cat.id} style={{ marginBottom: '22px' }}>
              <p style={cs.sectionTitle}>
                {cat.name}
                <span style={{ fontSize: '11px', color: P.muted, fontWeight: 400, marginLeft: '6px' }}>
                  {isSingle ? '— choose one' : '— select all that apply'}
                </span>
              </p>
              {cat.name === 'Removal' && (
                <p style={{ fontSize: '12px', color: P.muted, lineHeight: '1.6', marginBottom: '12px', fontStyle: 'italic' }}>
                  Removal methods vary depending on the service booked. Some services may involve complete soak-off removal, while others may involve reducing and rebalancing existing product for optimal retention and nail health.
                </p>
              )}

              {isSingle ? (
                /* Radio-style single select */
                catSvcs.map(svc => {
                  const isSelected = catSel === svc.id;
                  return (
                    <div key={svc.id} onClick={() => handleSingleSelect(cat.id, svc.id)}
                      style={{ padding: '14px', borderRadius: '10px', border: `1.5px solid ${isSelected ? P.rose : P.border}`, background: isSelected ? P.peach + '30' : P.white, marginBottom: '8px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', transition: 'all 0.15s', minHeight: '52px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        {/* Custom radio indicator */}
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${isSelected ? P.rose : P.border}`, background: isSelected ? P.rose : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px', transition: 'all 0.15s' }}>
                          {isSelected && <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#fff' }} />}
                        </div>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: '15px', color: isSelected ? P.rose : P.text }}>{svc.name}</span>
                          {Number(svc.duration) > 0 && <span style={{ marginLeft: '8px', fontSize: '12px', color: P.muted }}>⏰ {svc.duration} min</span>}
                          {svc.description && (
                            <p style={{ fontSize: '12px', color: P.muted, marginTop: '4px', marginBottom: 0, lineHeight: '1.5' }}>{svc.description}</p>
                          )}
                        </div>
                      </div>
                      {Number(svc.price) > 0 && (
                        <span style={{ color: P.rose, fontWeight: 700, flexShrink: 0, fontSize: '15px', marginLeft: '10px' }}>+${svc.price}</span>
                      )}
                    </div>
                  );
                })
              ) : (
                /* Checkbox-style multi select */
                catSvcs.map(svc => {
                  const isChecked = Array.isArray(catSel) && catSel.includes(svc.id);
                  const isQty = isQtyService(svc.name);
                  const qty = quantities[svc.id] || 1;
                  return (
                    <div key={svc.id} onClick={() => handleMultiToggle(cat.id, svc.id)}
                      style={{ padding: '14px', borderRadius: '10px', border: `1.5px solid ${isChecked ? P.rose : P.border}`, background: isChecked ? P.peach + '30' : P.white, marginBottom: '8px', cursor: 'pointer', display: 'flex', flexDirection: 'column', transition: 'all 0.15s', minHeight: '52px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {/* Custom checkbox indicator */}
                          <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: `2px solid ${isChecked ? P.rose : P.border}`, background: isChecked ? P.rose : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                            {isChecked && <span style={{ color: '#fff', fontSize: '11px', fontWeight: 800, lineHeight: 1 }}>✓</span>}
                          </div>
                          <div>
                            <span style={{ fontWeight: 700, fontSize: '15px', color: isChecked ? P.rose : P.text }}>{svc.name}</span>
                            {isQty && <span style={{ marginLeft: '6px', fontSize: '11px', color: P.muted, fontWeight: 400 }}>${svc.price}/nail</span>}
                            {Number(svc.duration) > 0 && !isQty && <span style={{ marginLeft: '8px', fontSize: '12px', color: P.muted }}>⏰ {svc.duration} min</span>}
                            {svc.description && (
                              <p style={{ fontSize: '12px', color: P.muted, marginTop: '4px', marginBottom: 0, lineHeight: '1.5' }}>{svc.description}</p>
                            )}
                          </div>
                        </div>
                        {Number(svc.price) > 0 && (
                          <span style={{ color: P.rose, fontWeight: 700, flexShrink: 0, fontSize: '15px', marginLeft: '10px' }}>
                            +${isQty && isChecked ? qty * Number(svc.price) : svc.price}
                          </span>
                        )}
                      </div>
                      {isChecked && isQty && (
                        <div onClick={e => e.stopPropagation()}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', paddingTop: '10px', borderTop: `1px dashed ${P.border}60` }}>
                          <span style={{ fontSize: '12px', color: P.muted, fontWeight: 600, flexShrink: 0 }}>Qty:</span>
                          <button onClick={() => setQuantities(q => ({ ...q, [svc.id]: Math.max(1, (q[svc.id] || 1) - 1) }))}
                            style={{ width: '32px', height: '32px', borderRadius: '50%', border: `1.5px solid ${P.border}`, background: P.white, cursor: 'pointer', fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, lineHeight: 1 }}>−</button>
                          <span style={{ fontSize: '17px', fontWeight: 800, color: P.deep, minWidth: '28px', textAlign: 'center' }}>{qty}</span>
                          <button onClick={() => setQuantities(q => ({ ...q, [svc.id]: Math.min(10, (q[svc.id] || 1) + 1) }))}
                            style={{ width: '32px', height: '32px', borderRadius: '50%', border: `1.5px solid ${P.border}`, background: P.white, cursor: 'pointer', fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, lineHeight: 1 }}>+</button>
                          <span style={{ fontSize: '12px', color: P.muted }}>nails &nbsp;·&nbsp; total: <strong style={{ color: P.rose }}>${qty * Number(svc.price)}</strong></span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          );
        })}

        {/* Selection summary */}
        {allSelectedServices.length > 0 && (
          <div style={{ background: P.peach + '25', borderRadius: '10px', padding: '12px 14px', marginBottom: '20px' }}>
            <p style={{ fontSize: '11px', color: P.muted, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your Selection</p>
            <p style={{ fontSize: '13px', color: P.text, margin: 0 }}>{selectedServiceNames}</p>
            {totalPrice > 0 && (
              <p style={{ fontWeight: 700, color: P.deep, margin: '4px 0 0', fontSize: '14px' }}>
                Total: ${totalPrice}{totalDuration > 0 ? ` · approx. ${totalDuration} min` : ''}
              </p>
            )}
            {totalDuration > 0 && (
              <p style={{ fontSize: '11px', color: P.muted, margin: '6px 0 0', fontStyle: 'italic' }}>
                Appointment times are approximate and may vary slightly.
              </p>
            )}
          </div>
        )}

        {/* Date, time, phone */}
        {serviceSelectionComplete && (
          <>
            <div style={{ marginBottom: '14px' }}>
              <label style={cs.label}>Preferred Date</label>
              <input type="date" min={minBookingDate} style={cs.input} value={selectedDate} onChange={e => { setSelectedDate(e.target.value); setSelectedTime(''); }} />
            </div>

            {selectedDate && (
              <div style={{ marginBottom: '14px' }}>
                <label style={cs.label}>Available Times</label>
                {availableSlots.length === 0 ? (
                  <p style={{ color: P.muted, fontSize: '13px', textAlign: 'center', padding: '10px 0' }}>No slots available on this date.</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {availableSlots.map(t => (
                      <button key={t} onClick={() => setSelectedTime(t)}
                        style={{ padding: '12px 8px', borderRadius: '8px', border: `1.5px solid ${selectedTime === t ? P.rose : P.border}`, background: selectedTime === t ? P.rose : P.white, color: selectedTime === t ? P.white : P.text, fontWeight: 600, cursor: 'pointer', fontSize: '15px', minHeight: '48px' }}>
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={cs.label}>Phone Number</label>
              <input type="tel" style={cs.input} placeholder="e.g. 04xx xxx xxx" value={clientPhone} onChange={e => setClientPhone(e.target.value)} />
              {clientPhone.trim().length > 0 && !isValidAustralianPhone(clientPhone) && (
                <p style={{ fontSize: '12px', color: P.red, marginTop: '6px' }}>⚠️ Please enter a valid Australian number (e.g. 0412 345 678)</p>
              )}
            </div>

            {/* Photo upload — authenticated users only */}
            {session && !isGuest && (
              <div style={{ marginBottom: '20px', padding: '16px', background: '#FDFCFB', borderRadius: '12px', border: `1px dashed ${P.border}` }}>
                <p style={{ ...cs.sectionTitle, marginBottom: '6px' }}>📸 Reference Photos <span style={{ color: P.muted, fontWeight: 400, fontSize: '12px' }}>(optional)</span></p>
                <p style={{ fontSize: '12px', color: P.muted, marginBottom: '12px', lineHeight: '1.5' }}>
                  Attach up to {MAX_PHOTOS} photos of your current nails or inspiration designs.<br />JPG · PNG · HEIC · max 10MB
                </p>
                {photoPreviews.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {photoPreviews.map((src, i) => (
                      <div key={i} style={{ position: 'relative', width: '72px', height: '72px' }}>
                        <img src={src} alt="" style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '8px', border: `1.5px solid ${P.border}` }} />
                        <button onClick={() => removePhoto(i)} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%', background: P.red, color: '#fff', border: 'none', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                {photos.length < MAX_PHOTOS && (
                  <>
                    <button onClick={() => photoInputRef.current?.click()}
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `1.5px dashed ${P.rose}80`, background: P.peach + '20', color: P.rose, fontWeight: 600, cursor: 'pointer', fontSize: '14px', minHeight: '44px' }}>
                      + Add Photo ({photos.length}/{MAX_PHOTOS})
                    </button>
                    <input ref={photoInputRef} type="file" accept=".jpg,.jpeg,.png,.heic,.heif" multiple style={{ display: 'none' }} onChange={handlePhotoChange} />
                  </>
                )}
              </div>
            )}

            <button onClick={() => setPageMode('payment')} disabled={!step3Done} style={cs.primaryBtn(!step3Done)}>
              Confirm & Pay Deposit
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function BookingPage({ session, onViewMyBookings, onFindBooking }) {
  return (
    <Elements stripe={stripePromise}>
      <BookingFlow session={session} onViewMyBookings={onViewMyBookings} onFindBooking={onFindBooking} />
    </Elements>
  );
}
